import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Alert,
  CircularProgress,
  Avatar,
  Chip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PhotoCamera as PhotoCameraIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  getUserProfile,
  getOrCreateUserProfile,
  updateUserProfile,
  uploadUserPhoto,
  notifyAdminPhotoUpload,
  NTRP_LEVELS,
  getCoachByUid,
  getCoachProfile,
  updateCoachProfile,
  PRICING
} from '../utils/firebaseUtils';
import { isCoach, isUserAdmin } from '../utils/adminUtils';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile, setUserProfile, approvedCoachUids } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [ntrpExpanded, setNtrpExpanded] = useState(false);
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    ntrpLevel: '',
    ntrpInfo: '',
    coachDescription: '',
    price1Person: '',
    price2People: '',
    price3People: '',
    price4People: ''
  });

  useEffect(() => {
    if (!currentUser || currentUser.isAnonymous) {
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        let p = await getUserProfile(currentUser.uid);
        if (!p) {
          p = await getOrCreateUserProfile(currentUser.uid, {
            email: currentUser.email || '',
            fullName: currentUser.displayName || '',
            phoneNumber: userProfile?.phoneNumber || ''
          });
        }
        setProfile(p);
        const coach = getCoachByUid(currentUser.uid) || (approvedCoachUids?.includes(currentUser.uid) ? { id: currentUser.uid, uid: currentUser.uid, defaultDescription: '' } : null);
        let coachDesc = '';
        let price1 = '', price2 = '', price3 = '', price4 = '';
        if (coach) {
          const coachProfile = await getCoachProfile(coach.id);
          coachDesc = coachProfile?.description ?? coach?.defaultDescription ?? '';
          price1 = coachProfile?.price1Person ?? PRICING.ONE_PERSON_PER_HOUR;
          price2 = coachProfile?.price2People ?? PRICING.TWO_PEOPLE_PER_HOUR;
          price3 = coachProfile?.price3People ?? PRICING.THREE_FOUR_PERSONS_PER_HOUR;
          price4 = coachProfile?.price4People ?? PRICING.THREE_FOUR_PERSONS_PER_HOUR;
        }
        setForm({
          fullName: p?.fullName ?? currentUser.displayName ?? '',
          email: p?.email ?? currentUser.email ?? '',
          phoneNumber: p?.phoneNumber ?? userProfile?.phoneNumber ?? '',
          ntrpLevel: p?.ntrpLevel ?? '',
          ntrpInfo: p?.ntrpInfo ?? '',
          coachDescription: coachDesc,
          price1Person: price1,
          price2People: price2,
          price3People: price3,
          price4People: price4
        });
        // Ensure email is stored for admin list (existing profiles may lack it)
        if (p && !p.email && currentUser.email) {
          try {
            await updateUserProfile(currentUser.uid, { email: currentUser.email });
          } catch (_) {}
        }
      } catch (err) {
        console.error(err);
        setError('Nepavyko įkelti profilio');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser, userProfile?.phoneNumber, approvedCoachUids]);

  const handleSave = async () => {
    if (!currentUser || currentUser.isAnonymous) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await updateUserProfile(currentUser.uid, {
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phoneNumber: form.phoneNumber.trim(),
        ntrpLevel: form.ntrpLevel,
        ntrpInfo: form.ntrpInfo.trim()
      });
      const coach = getCoachByUid(currentUser.uid) || (approvedCoachUids?.includes(currentUser.uid) ? { id: currentUser.uid, uid: currentUser.uid } : null);
      if (coach) {
        const coachUpdates = {
          description: form.coachDescription.trim(),
          email: form.email.trim(),
          phone: form.phoneNumber.trim(),
          price1Person: Number(form.price1Person) || PRICING.ONE_PERSON_PER_HOUR,
          price2People: Number(form.price2People) || PRICING.TWO_PEOPLE_PER_HOUR,
          price3People: Number(form.price3People) || PRICING.THREE_FOUR_PERSONS_PER_HOUR,
          price4People: Number(form.price4People) || PRICING.THREE_FOUR_PERSONS_PER_HOUR
        };
        await updateCoachProfile(coach.id, coachUpdates);
      }
      const updated = await getUserProfile(currentUser.uid);
      setProfile(updated);
      setUserProfile(updated);
      setSuccess('Profilis išsaugotas');
    } catch (err) {
      console.error(err);
      setError('Nepavyko išsaugoti');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser || currentUser.isAnonymous) return;
    if (!file.type.startsWith('image/')) {
      setError('Pasirinkite nuotraukos failą');
      return;
    }
    setUploadingPhoto(true);
    setError('');
    try {
      const isCoachUser = isCoach(currentUser, approvedCoachUids);
      const url = await uploadUserPhoto(currentUser.uid, file, { noCompression: isCoachUser });
      await updateUserProfile(currentUser.uid, { photoURL: url });
      const updated = await getUserProfile(currentUser.uid);
      setProfile(updated);
      setUserProfile(updated);
      const coach = getCoachByUid(currentUser.uid) || (approvedCoachUids?.includes(currentUser.uid) ? { id: currentUser.uid, uid: currentUser.uid } : null);
      if (coach) {
        await updateCoachProfile(coach.id, { photoURL: url });
      }
      setSuccess('Nuotrauka atnaujinta');
      await notifyAdminPhotoUpload({
        fullName: updated?.fullName || currentUser.displayName || '',
        email: updated?.email || currentUser.email || ''
      });
    } catch (err) {
      console.error(err);
      setError('Nepavyko įkelti nuotraukos');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemoveFromCoachesList = () => {
    navigate('/delete-account', { replace: true });
  };

  if (!currentUser) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Typography color="text.secondary">Prisijunkite, kad matytumėte profilį.</Typography>
      </Container>
    );
  }

  if (currentUser.isAnonymous) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Typography color="text.secondary">Profilis prieinamas tik registruotiems vartotojams.</Typography>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="sm">
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, letterSpacing: '-0.02em' }}>
          Profilis
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Paper sx={{ p: 3, borderRadius: 3, mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          {/* Photo upload */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Nuotrauka
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Avatar
              src={profile?.photoURL}
              sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}
            >
              {profile?.fullName?.[0]?.toUpperCase() || currentUser.email?.[0]?.toUpperCase()}
            </Avatar>
            <Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={uploadingPhoto ? <CircularProgress size={20} /> : <PhotoCameraIcon />}
                disabled={uploadingPhoto}
                sx={{ borderRadius: 2 }}
              >
                {uploadingPhoto ? 'Kraunama...' : 'Įkelti nuotrauką'}
                <input type="file" accept="image/*" hidden onChange={handlePhotoChange} />
              </Button>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                Nuotrauka bus suspausta ir įkelta į saugyklą.
              </Typography>
            </Box>
          </Box>

          <TextField
            fullWidth
            label="Vardas ir pavardė"
            value={form.fullName}
            onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextField
            fullWidth
            label="El. paštas"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="example@email.com"
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextField
            fullWidth
            label="Telefono numeris"
            value={form.phoneNumber}
            onChange={(e) => setForm((f) => ({ ...f, phoneNumber: e.target.value }))}
            placeholder="+370..."
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          {/* Coach section: Jūsų informacija + description */}
          {isCoach(currentUser, approvedCoachUids) && (
            <Box sx={{ mb: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Jūsų informacija
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 100 }}>
                    Vartotojo ID:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {currentUser?.uid}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 100 }}>
                    Rolė:
                  </Typography>
                  <Chip
                    label={isUserAdmin(currentUser) ? 'Administratorius' : 'Treneris'}
                    color="success"
                    size="small"
                  />
                </Box>
              </Box>

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
                Aprašymas (rodomas svetainės pagrindiniame puslapyje)
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={4}
                placeholder="Įrašykite apie save – patirtį, pasiekimus, požiūrį į treniruotes. Šis tekstas bus rodomas klientams, kai spustelės jūsų kortelę."
                value={form.coachDescription}
                onChange={(e) => setForm((f) => ({ ...f, coachDescription: e.target.value }))}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
                Kainos (€/val.)
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
                <TextField
                  type="number"
                  label="1 asmuo"
                  value={form.price1Person}
                  onChange={(e) => setForm((f) => ({ ...f, price1Person: e.target.value }))}
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  type="number"
                  label="2 asmenys"
                  value={form.price2People}
                  onChange={(e) => setForm((f) => ({ ...f, price2People: e.target.value }))}
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  type="number"
                  label="3 asmenys"
                  value={form.price3People}
                  onChange={(e) => setForm((f) => ({ ...f, price3People: e.target.value }))}
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  type="number"
                  label="4 asmenys"
                  value={form.price4People}
                  onChange={(e) => setForm((f) => ({ ...f, price4People: e.target.value }))}
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>
            </Box>
          )}

          {!isCoach(currentUser, approvedCoachUids) && (
            <>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="ntrp-label">NTRP lygis</InputLabel>
            <Select
              labelId="ntrp-label"
              label="NTRP lygis"
              value={form.ntrpLevel}
              onChange={(e) => setForm((f) => ({ ...f, ntrpLevel: e.target.value }))}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="">Nepasirinkta</MenuItem>
              {NTRP_LEVELS.map((level) => (
                <MenuItem key={level} value={level}>
                  {level}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Expandable NTRP reference */}
          <Box sx={{ mb: 2 }}>
            <Button
              fullWidth
              onClick={() => setNtrpExpanded((e) => !e)}
              endIcon={ntrpExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ justifyContent: 'space-between', textTransform: 'none', fontWeight: 600 }}
            >
              NTRP lygių aprašymai
            </Button>
            <Collapse in={ntrpExpanded}>
              <Paper variant="outlined" sx={{ mt: 1, p: 2, borderRadius: 2, bgcolor: 'grey.50', maxHeight: 420, overflow: 'auto' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>NTRP</Typography>
                {[
                  { level: '1.0', text: 'Jūs dar tik pradėjote žaisti tenisą.' },
                  { level: '1.5', text: 'Turite nedidelę žaidimo patirtį ir vis dar daugiausia dirbate siekdamas, jog teniso kamuoliukas perskrietų į kitą aikštelės pusę. Jūs dar nesate pasirengęs varžytis su kitais žaidėjais.' },
                  { level: '2.0', text: 'Vis dar trūksta patirties teniso aikštelėje, daugiausia dėmesio skiriate žaidimo tobulinimui. Jums vis dar sunku surasti tinkamą smūgio tašką ir dar reikia tobulinti smūgiavimo techniką. Nesate susipažinęs su pagrindinėmis vienetų ir dvejetų žaidimo pozicijomis.' },
                  { level: '2.5', text: 'Mokotės teisingai įvertinti, kur teniso kamuoliukas atšoks ir kiek jėgos reikia, kad galėtumėte pastoviai pataikyti į priešininko aikštelės pusę. Jūsų judėjimas link kamuoliuko ir sugrįžimas po smūgio dažnai nėra efektyvus. Galite lėtai atmušti teniso kamuoliuką be klaidų daugiau nei kelis kartus su kitais panašaus pajėgumo žaidėjais ir pradedate įvaldyti smūgius nuo galinės linijos. Jūs vis labiau susipažįstate su pagrindinėmis vienetų ir dvejetų žaidimo pozicijomis ir esate pasirengęs varžytis su kitais, dalyvauti žemo lygio turnyruose.' },
                  { level: '3.0', text: 'Sugebate gana nuosekliai atlikti vidutinio stiprumo smūgius, tačiau Jums vis dar trūksta tikslumo ir patirties atliekant įvairius smūgius – keičiant smūgiavimo kryptį, valdant smūgiavimo tempą, smūgiuojant arti galinės linijos. Dažniausiai žaisdami naudojate dvejetų formatą, kai vienas žaidėjas yra gale, kitas prie tinklo.' },
                  { level: '3.5', text: 'Pavyksta patikimai smūgiuoti ir nukreipti vidutinio stiprumo smūgius, tačiau klystate įvairiai keisdami kamuoliuko distancijos gylį nuo tinklo iki galinės linijos. Aukštas suktas smūgis, žvakė arba mušimas teniso kamuoliukui nepalietus žemės vis dar yra ribotas. Jūs žaidžiate drąsiai prie tinklo, geriau jaučiate teniso aikštelę ir ugdote komandinį žaidimą dvejetuose.' },
                  { level: 'Aukštesni lygiai', text: null },
                  { level: '4.0', text: 'Galite užtikrintai atlikti smūgius su krypties valdymu ir galite keisti smūgių gylį tiek arčiau, tiek toliau galinės linijos vykstant žaidimui vidutiniu tempu. Jūs galite sėkmingai panaudoti žvakę arba aukštą suktą smūgį apvedant žaidėją, atlikti mušimą teniso kamuoliukui nepalietus žemės. Jūs vis dar darote klaidas servuojant. Taškus prarandate dėl nekantrumo. Komandinis darbas dvejetuose yra akivaizdus.' },
                  { level: '4.5', text: 'Sugebate keisti žaidimo tempą ir kamuoliuko sukimą, turite užtikrintą teniso aikštelės matymą ir galite kontroliuoti smūgius arti galinės linijos. Taip pat galite kurti žaidimo planus pagal stipriąsias ir silpnąsias žaidėjų puses. Jūs galite galingai ir tiksliai pataikyti pirmąjį padavimą ir galite tiksliai atliktii antrą padavimą. Jūs vis dar klystate atlikdami sudėtingus smūgius juos per mušdami už aikštelės ribų. Agresyvus žaidimas tinkle yra įprastas dvejetuose.' },
                  { level: '5.0', text: 'Turite gerai išlavinęs savo stipriąsias puses, kuriomis galite pasinaudoti struktūrizuojant žaidimo eigą. Jūs gebate ir galite reguliariai pataikyti „winners” smūgius arba priversti varžovą daryti klaidas trumpinant arba pjaunant teniso kamuoliuką. Galite sėkmingai atlikti smūgius iš oro aikštelės viduryje, apvesti žaidėją, atlikti „smash“. Jūsų antras padavimas yra stipriai suktas ir arti padavimo linijos.' },
                  { level: '5.5', text: 'Esate puikiai išvystęs smūgiavimą greitu tempu ir nuoseklumą galite naudoti kaip pagrindinį ginklą. Jūs galite keisti žaidimo strategiją ir stilių sudėtingose situacijose ir pataikyti patikimus smūgius stresinėse situacijose.' },
                  { level: '6.0', text: 'Intensyviai treniruojatės ir žaidžiate nacionaliniuose turnyruose arba aukščiausio lygio varžybose ir turite nacionalinį reitingą.' },
                  { level: '6.5. – 7.0', text: 'Jūs esate pasaulinio lygio žaidėjas.' }
                ].map((item, idx) => (
                  <Box key={idx} sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.level}.</Typography>
                    {item.text && <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, lineHeight: 1.5 }}>{item.text}</Typography>}
                  </Box>
                ))}
              </Paper>
            </Collapse>
          </Box>
            </>
          )}

          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ mt: 3, borderRadius: 2, fontWeight: 600 }}
          >
            {saving ? 'Saugoma...' : 'Išsaugoti'}
          </Button>

          {isCoach(currentUser, approvedCoachUids) && (
            <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider', p: 2, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Išbraukti mane iš trenerių sąrašo
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Nukreips į paskyros ištrynimo puslapį. Ten, patvirtinę, galėsite ištrinti paskyrą (bus pašalinti ir iš trenerių sąrašo, ir laisvų laikų duomenys).
              </Typography>
              <Button
                variant="outlined"
                color="error"
                onClick={handleRemoveFromCoachesList}
                sx={{ textTransform: 'none' }}
              >
                Išbraukti mane iš trenerių sąrašo
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ProfilePage;
